import {User} from 'firebase/auth';
import {QueryClient, UseQueryResult, useMutation, useQuery, useQueryClient} from 'react-query';

import {
  ApiUserProfile,
  ApiUserSettings,
  fetchCurrentUser,
  updateUserSettings,
} from 'src/lib/api/auth';

/**
 * If the user is logged in, loads and returns the ApiOrganizationUser record we
 * have stored in Firebase.
 *
 * Returns null if user is not logged in as well as during the loading process.
 */
export default function useUserProfileQuery(
  firebaseUser: User | null | undefined
): UseQueryResult<ApiUserProfile> {
  const uid = firebaseUser && firebaseUser.uid;
  return useQuery(['userProfile', {uid}], fetchCurrentUser, {
    enabled: !!uid,
  });
}

/**
 * Patches the cached profile (the query AuthProvider consumes, which drives
 * the header, permission gating, locale, etc.), then refetches so the cache
 * converges on the server's view.
 */
export function patchCachedUserProfile(
  queryClient: QueryClient,
  patch: (profile: ApiUserProfile) => ApiUserProfile
) {
  queryClient.setQueriesData<ApiUserProfile | undefined>('userProfile', (prev) =>
    prev ? patch(prev) : prev
  );
  queryClient.invalidateQueries('userProfile');
}

/**
 * Saves the current user's settings and writes the result into the cached
 * profile, so auth.userProfile reflects the change immediately.
 */
export function useUpdateUserSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation((settings: ApiUserSettings) => updateUserSettings(settings), {
    onSuccess: (data) => {
      if (!data?.settings) {
        return;
      }
      patchCachedUserProfile(queryClient, (profile) => ({...profile, settings: data.settings}));
    },
  });
}
